import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDebug } from '../contexts/DebugContext';
import { getCachedAccountData } from '../shared/accountCache';
import SlidingMenu from '../components/SlidingMenu';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';

export default function DebugSettings() {
  const { currentUser } = useAuth();
  const { debugMode, toggleDebugMode } = useDebug();
  const [showCopied, setShowCopied] = useState(false);
  const [localStorageItems, setLocalStorageItems] = useState([]);
  const [templateVersion, setTemplateVersion] = useState('v1');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [isProcessingPlan, setIsProcessingPlan] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [paymentProvider, setPaymentProvider] = useState('lemonsqueezy');
  const [isSavingProvider, setIsSavingProvider] = useState(false);
  const [providerSaveResult, setProviderSaveResult] = useState(null);
  const [aiProviderFree, setAiProviderFree] = useState('GROQ');
  const [aiProviderPro, setAiProviderPro] = useState('GROQ');
  const [isSavingAiProviders, setIsSavingAiProviders] = useState(false);
  const [aiProvidersSaveResult, setAiProvidersSaveResult] = useState(null);
  const [sendMailOnContactUs, setSendMailOnContactUs] = useState(true);
  const [isSavingContactUsSetting, setIsSavingContactUsSetting] = useState(false);
  const [contactUsSettingSaveResult, setContactUsSettingSaveResult] = useState(null);
  const navigate = useNavigate();
  
  // Check if user is admin
  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const account = await getCachedAccountData();
        if (!account || account.subscription?.tier !== 'admin') {
          navigate('/plans');
          return;
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/plans');
        return;
      } finally {
        setLoading(false);
      }
    };
    
    checkAdmin();
  }, [currentUser, navigate]);
  
  // Load all localStorage items and template version
  useEffect(() => {
    // Load localStorage items
    const items = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      try {
        const value = localStorage.getItem(key);
        items.push({ key, value });
      } catch (e) {
        items.push({ key, value: "Error reading value" });
      }
    }
    setLocalStorageItems(items);
    
    // Load template version from localStorage
    const savedTemplateVersion = localStorage.getItem('tripsy_template_version');
    if (savedTemplateVersion) {
      setTemplateVersion(savedTemplateVersion);
    }
  }, []);

  // Fetch plans for the admin feature
  useEffect(() => {
    if (currentUser && !loading) {
      fetchPlans();
    }
  }, [currentUser, loading]);

  // Fetch current payment provider setting
  useEffect(() => {
    if (currentUser && !loading) {
      fetchPaymentProvider();
      fetchAiProviders();
      fetchContactUsSetting();
    }
  }, [currentUser, loading]);

  const fetchPlans = async () => {
    try {
      const response = await apiCall("/api/model?action=" + ApiActions.GET_PLANS, {}, 'POST');
      if (response.success) {
        setPlans(response.plans || []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchPaymentProvider = async () => {
    try {
      const response = await apiCall(
        `/api/model?action=${ApiActions.GET_SETTINGS}&key=PAYMENT_PROVIDER`
      );
      if (response.success && response.data) {
        setPaymentProvider(response.data);
      }
    } catch (error) {
      console.error('Error fetching payment provider:', error);
    }
  };

  const fetchAiProviders = async () => {
    try {
      // Fetch free tier AI provider
      const freeResponse = await apiCall(
        `/api/model?action=${ApiActions.GET_SETTINGS}&key=AI_PROVIDER_FREE`
      );
      if (freeResponse.success && freeResponse.data) {
        setAiProviderFree(freeResponse.data);
      }

      // Fetch pro tier AI provider
      const proResponse = await apiCall(
        `/api/model?action=${ApiActions.GET_SETTINGS}&key=AI_PROVIDER_PRO`
      );
      if (proResponse.success && proResponse.data) {
        setAiProviderPro(proResponse.data);
      }
    } catch (error) {
      console.error('Error fetching AI providers:', error);
    }
  };

  const fetchContactUsSetting = async () => {
    try {
      const response = await apiCall(
        `/api/model?action=${ApiActions.GET_SETTINGS}&key=SendMailToAdminOnNewContactUsRequest`
      );
      if (response.success && response.data !== undefined) {
        // Convert string to boolean
        setSendMailOnContactUs(response.data === 'true' || response.data === true);
      } else {
        // Default to true if setting doesn't exist
        setSendMailOnContactUs(true);
      }
    } catch (error) {
      console.error('Error fetching contact us setting:', error);
      // Default to true on error
      setSendMailOnContactUs(true);
    }
  };

  const handleSaveContactUsSetting = async () => {
    setIsSavingContactUsSetting(true);
    setContactUsSettingSaveResult(null);

    try {
      const response = await apiCall(
        `/api/model?action=${ApiActions.UPDATE_SETTINGS}`,
        {
          key: 'SendMailToAdminOnNewContactUsRequest',
          type: 'string',
          value: String(sendMailOnContactUs)
        },
        'POST'
      );

      if (response.success) {
        setContactUsSettingSaveResult({ success: true, message: 'Contact Us email setting updated successfully!' });
      } else {
        setContactUsSettingSaveResult({ success: false, message: response.message || 'Failed to save setting.' });
      }
    } catch (error) {
      console.error('Error saving contact us setting:', error);
      setContactUsSettingSaveResult({ success: false, message: 'An error occurred while saving.' });
    } finally {
      setIsSavingContactUsSetting(false);
    }
  };

  const handleSavePaymentProvider = async () => {
    setIsSavingProvider(true);
    setProviderSaveResult(null);

    try {
      const response = await apiCall(
        `/api/model?action=${ApiActions.UPDATE_SETTINGS}`,
        {
          key: 'PAYMENT_PROVIDER',
          type: 'string',
          value: paymentProvider
        },
        'POST'
      );

      if (response.success) {
        setProviderSaveResult({ success: true, message: 'Payment provider updated successfully!' });
      } else {
        setProviderSaveResult({ success: false, message: response.message || 'Failed to save payment provider.' });
      }
    } catch (error) {
      console.error('Error saving payment provider:', error);
      setProviderSaveResult({ success: false, message: 'An error occurred while saving.' });
    } finally {
      setIsSavingProvider(false);
    }
  };

  const handleSaveAiProviders = async () => {
    setIsSavingAiProviders(true);
    setAiProvidersSaveResult(null);

    try {
      // Save free tier provider
      const freeResponse = await apiCall(
        `/api/model?action=${ApiActions.UPDATE_SETTINGS}`,
        {
          key: 'AI_PROVIDER_FREE',
          type: 'string',
          value: aiProviderFree
        },
        'POST'
      );

      // Save pro tier provider
      const proResponse = await apiCall(
        `/api/model?action=${ApiActions.UPDATE_SETTINGS}`,
        {
          key: 'AI_PROVIDER_PRO',
          type: 'string',
          value: aiProviderPro
        },
        'POST'
      );

      if (freeResponse.success && proResponse.success) {
        setAiProvidersSaveResult({ success: true, message: 'AI providers updated successfully!' });
      } else {
        setAiProvidersSaveResult({ success: false, message: 'Failed to save AI providers.' });
      }
    } catch (error) {
      console.error('Error saving AI providers:', error);
      setAiProvidersSaveResult({ success: false, message: 'An error occurred while saving.' });
    } finally {
      setIsSavingAiProviders(false);
    }
  };

  // Function to create Google Maps link from activity data
  const createGoogleMapsLink = (activity) => {
    if (!activity.name) return null;
    
    const loc = activity.location;
    let searchQuery = activity.name;
    if (loc && typeof loc === 'object' && (loc.city || loc.country)) {
      const parts = [];
      if (loc.city) parts.push(loc.city);
      if (loc.country) parts.push(loc.country);
      searchQuery += `, ${parts.join(', ')}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`;
  };

  // Function to create Google Maps link from location data (for transportation)
  const createGoogleMapsLinkFromLocation = (location) => {
    if (!location) return null;
    
    let searchQuery = location.address || '';
    if (location && typeof location === 'object' && (location.city || location.country)) {
      const parts = [];
      if (location.address) parts.push(location.address);
      if (location.city) parts.push(location.city);
      if (location.country) parts.push(location.country);
      searchQuery = parts.join(', ');
    }
    
    if (!searchQuery || !searchQuery.trim()) return null;
    
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`;
  };

  // Function to add Google Maps links to all activities in a plan
  const handleAddGoogleMapsLinks = async () => {
    if (!selectedPlanId) {
      setProcessResult({ success: false, message: 'Please select a plan first.' });
      return;
    }

    setIsProcessingPlan(true);
    setProcessResult(null);

    try {
      // Fetch the plan
      const planResponse = await apiCall(`/api/model?action=${ApiActions.GET_PLAN_BY_ID}&planId=${selectedPlanId}`);
      
      if (!planResponse.success || !planResponse.plan) {
        setProcessResult({ success: false, message: 'Failed to load plan.' });
        setIsProcessingPlan(false);
        return;
      }

      // Parse the plan data
      const planData = JSON.parse(planResponse.plan.data);
      let updatedCount = 0;
      let skippedCount = 0;

      // Loop through all days and activities
      if (planData.days && Array.isArray(planData.days)) {
        planData.days.forEach(day => {
          if (day.activities && Array.isArray(day.activities)) {
            day.activities.forEach(activity => {
              // Initialize links array if it doesn't exist
              if (!activity.links) {
                activity.links = [];
              }
              
              // Process accommodation and attraction activities
              if (activity.type === 'accommodation' || activity.type === 'attraction') {
                // Check if Google Maps link already exists
                const hasGoogleMapsLink = activity.links.some(link => 
                  link.name === 'Google Map' || link.name === 'google map'
                );
                
                if (!hasGoogleMapsLink) {
                  const googleMapsUrl = createGoogleMapsLink(activity);
                  if (googleMapsUrl) {
                    // Add Google Maps link
                    activity.links.push({
                      name: "Google Map",
                      url: googleMapsUrl,
                      isPrivate: false
                    });
                    updatedCount++;
                  } else {
                    skippedCount++;
                  }
                } else {
                  skippedCount++;
                }
              }
              
              // Process transportation activities
              if (activity.type === 'transportation') {
                let activityUpdated = false;
                
                // Add "Google Map From" link
                if (activity.fromLocation) {
                  const hasGoogleMapsFromLink = activity.links.some(link => 
                    link.name === 'Google Map From' || link.name === 'google map from'
                  );
                  
                  if (!hasGoogleMapsFromLink) {
                    const googleMapsFromUrl = createGoogleMapsLinkFromLocation(activity.fromLocation);
                    if (googleMapsFromUrl) {
                      activity.links.push({
                        name: "Google Map From",
                        url: googleMapsFromUrl,
                        isPrivate: false
                      });
                      activityUpdated = true;
                    }
                  }
                }
                
                // Add "Google Map To" link
                if (activity.toLocation) {
                  const hasGoogleMapsToLink = activity.links.some(link => 
                    link.name === 'Google Map To' || link.name === 'google map to'
                  );
                  
                  if (!hasGoogleMapsToLink) {
                    const googleMapsToUrl = createGoogleMapsLinkFromLocation(activity.toLocation);
                    if (googleMapsToUrl) {
                      activity.links.push({
                        name: "Google Map To",
                        url: googleMapsToUrl,
                        isPrivate: false
                      });
                      activityUpdated = true;
                    }
                  }
                }
                
                if (activityUpdated) {
                  updatedCount++;
                } else {
                  skippedCount++;
                }
              }
            });
          }
        });
      }

      // Save the updated plan
      const updateResponse = await apiCall("/api/model?action=" + ApiActions.UPDATE_PLAN, {
        planId: selectedPlanId,
        data: JSON.stringify(planData),
        version: planResponse.plan.version || 1
      }, 'POST');

      if (updateResponse.success) {
        setProcessResult({ 
          success: true, 
          message: `Successfully processed plan. Updated ${updatedCount} activities, skipped ${skippedCount} activities.` 
        });
      } else {
        setProcessResult({ success: false, message: 'Failed to save updated plan.' });
      }
    } catch (error) {
      console.error('Error processing plan:', error);
      setProcessResult({ success: false, message: `Error: ${error.message}` });
    } finally {
      setIsProcessingPlan(false);
    }
  };
  
  // Update template version in localStorage
  const handleTemplateVersionChange = (e) => {
    const version = e.target.value;
    setTemplateVersion(version);
    localStorage.setItem('tripsy_template_version', version);
    // Refresh localStorage items list
    const updatedItems = [...localStorageItems];
    const existingIndex = updatedItems.findIndex(item => item.key === 'tripsy_template_version');
    if (existingIndex >= 0) {
      updatedItems[existingIndex].value = version;
    } else {
      updatedItems.push({ key: 'tripsy_template_version', value: version });
    }
    setLocalStorageItems(updatedItems);
  };
  
  // Clear a specific localStorage item
  const clearItem = (key) => {
    if (window.confirm(`Are you sure you want to clear "${key}" from localStorage?`)) {
      localStorage.removeItem(key);
      // Refresh the list
      const items = localStorageItems.filter(item => item.key !== key);
      setLocalStorageItems(items);
    }
  };
  
  // Clear all localStorage
  const clearAllStorage = () => {
    if (window.confirm("Are you sure you want to clear ALL localStorage items? This will log you out.")) {
      localStorage.clear();
      setLocalStorageItems([]);
      // Redirect to home after clearing
      navigate('/');
    }
  };
  
  // Copy localStorage to clipboard
  const copyToClipboard = () => {
    const text = JSON.stringify(
      Object.fromEntries(localStorageItems.map(item => [item.key, item.value])),
      null, 2
    );
    navigator.clipboard.writeText(text);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };
  
  if (!currentUser || loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Checking access...</p>
      </Container>
    );
  }
  
  return (
    <div className="d-flex">
      <SlidingMenu title="Admin Debug Settings" />
      
      <div className="flex-grow-1 content-area">
        <Container className="py-4">
          <h1 className="mb-4">Debug Settings</h1>
          <p className="text-muted">This page is for administrator use only. It controls debug settings for the application.</p>
          
          {showCopied && (
            <Alert variant="success" dismissible onClose={() => setShowCopied(false)}>
              localStorage data copied to clipboard!
            </Alert>
          )}
          
          <Card className="mb-4">
            <Card.Header>Debug Mode Settings</Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="switch"
                    id="debug-mode-switch"
                    label={`Debug Mode: ${debugMode ? 'ENABLED' : 'DISABLED'}`}
                    checked={debugMode}
                    onChange={toggleDebugMode}
                  />
                  <Form.Text className="text-muted">
                    When enabled, all console.log, console.warn, and console.info messages will be shown.
                    When disabled, these messages are suppressed. console.error messages are always shown.
                  </Form.Text>
                </Form.Group>
                
                <div className="d-flex justify-content-between">
                  <Button 
                    variant={debugMode ? "success" : "secondary"}
                    onClick={toggleDebugMode}
                  >
                    {debugMode ? "Disable Debug Mode" : "Enable Debug Mode"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
          
          <Card className="mb-4">
            <Card.Header>AI Template Settings</Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Template Version</Form.Label>
                  <Form.Select 
                    value={templateVersion}
                    onChange={handleTemplateVersionChange}
                    aria-label="Select template version"
                  >
                    <option value="v1">Version 1 (Default)</option>
                    <option value="v2">Version 2 (Custom)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select which version of AI templates to use for trip planning. This setting persists across sessions.
                  </Form.Text>
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
          
          <Card className="mb-4">
            <Card.Header>Payment Provider Settings</Card.Header>
            <Card.Body>
              {providerSaveResult && (
                <Alert 
                  variant={providerSaveResult.success ? 'success' : 'danger'} 
                  dismissible 
                  onClose={() => setProviderSaveResult(null)}
                  className="mb-3"
                >
                  {providerSaveResult.message}
                </Alert>
              )}
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Active Payment Provider</Form.Label>
                  <Form.Select 
                    value={paymentProvider}
                    onChange={(e) => setPaymentProvider(e.target.value)}
                    aria-label="Select payment provider"
                  >
                    <option value="stripe">Stripe</option>
                    <option value="lemonsqueezy">Lemon Squeezy 🍋</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select which payment provider to use for new subscriptions. Existing subscriptions are not affected.
                  </Form.Text>
                </Form.Group>
                <Button 
                  variant="primary" 
                  onClick={handleSavePaymentProvider}
                  disabled={isSavingProvider}
                >
                  {isSavingProvider ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    'Save Payment Provider'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
          
          <Card className="mb-4">
            <Card.Header>AI Provider Settings (Per Tier)</Card.Header>
            <Card.Body>
              {aiProvidersSaveResult && (
                <Alert 
                  variant={aiProvidersSaveResult.success ? 'success' : 'danger'} 
                  dismissible 
                  onClose={() => setAiProvidersSaveResult(null)}
                  className="mb-3"
                >
                  {aiProvidersSaveResult.message}
                </Alert>
              )}
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>AI Provider for Free Users</Form.Label>
                  <Form.Select 
                    value={aiProviderFree}
                    onChange={(e) => setAiProviderFree(e.target.value)}
                    aria-label="Select AI provider for free users"
                  >
                    <option value="GROQ">Groq (Free)</option>
                    <option value="DEEPSEEK">DeepSeek</option>
                    <option value="XAIPAYING">X.AI</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    AI provider used for all Free tier users
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>AI Provider for Pro Users</Form.Label>
                  <Form.Select 
                    value={aiProviderPro}
                    onChange={(e) => setAiProviderPro(e.target.value)}
                    aria-label="Select AI provider for pro users"
                  >
                    <option value="GROQ">Groq (Free)</option>
                    <option value="DEEPSEEK">DeepSeek</option>
                    <option value="XAIPAYING">X.AI</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    AI provider used for all Pro and Admin tier users
                  </Form.Text>
                </Form.Group>

                <Button 
                  variant="primary" 
                  onClick={handleSaveAiProviders}
                  disabled={isSavingAiProviders}
                >
                  {isSavingAiProviders ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    'Save AI Providers'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
          
          <Card className="mb-4">
            <Card.Header>Contact Us Email Settings</Card.Header>
            <Card.Body>
              {contactUsSettingSaveResult && (
                <Alert 
                  variant={contactUsSettingSaveResult.success ? 'success' : 'danger'} 
                  dismissible 
                  onClose={() => setContactUsSettingSaveResult(null)}
                  className="mb-3"
                >
                  {contactUsSettingSaveResult.message}
                </Alert>
              )}
              <Form>
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="switch"
                    id="send-mail-on-contact-us"
                    label={`Send Email to Admin on New Contact Us Request: ${sendMailOnContactUs ? 'ENABLED' : 'DISABLED'}`}
                    checked={sendMailOnContactUs}
                    onChange={(e) => setSendMailOnContactUs(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    When enabled, admins will receive an email notification when a registered user submits a contact message through the footer.
                  </Form.Text>
                </Form.Group>
                <Button 
                  variant="primary" 
                  onClick={handleSaveContactUsSetting}
                  disabled={isSavingContactUsSetting}
                >
                  {isSavingContactUsSetting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    'Save Contact Us Email Setting'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
          
          <Card className="mb-4">
            <Card.Header>LocalStorage Management</Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Button 
                  variant="info" 
                  className="me-2"
                  onClick={copyToClipboard}
                >
                  Copy All to Clipboard
                </Button>
                <Button 
                  variant="danger"
                  onClick={clearAllStorage}
                >
                  Clear All Storage
                </Button>
              </div>
              
              <h5>LocalStorage Items ({localStorageItems.length})</h5>
              <div className="border rounded p-3" style={{ maxHeight: '300px', overflow: 'auto' }}>
                {localStorageItems.length === 0 ? (
                  <p className="text-muted mb-0">No items in localStorage</p>
                ) : (
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Value</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localStorageItems.map((item, index) => (
                        <tr key={index}>
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.key}
                          </td>
                          <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.value?.length > 50 ? `${item.value.substring(0, 50)}...` : item.value}
                          </td>
                          <td>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => clearItem(item.key)}
                            >
                              Clear
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Card.Body>
          </Card>
          
          <Card className="mb-4">
            <Card.Header>Add Google Maps Links (One-Time Feature)</Card.Header>
            <Card.Body>
              <Alert variant="warning">
                <strong>One-time feature:</strong> This will add Google Maps links to all accommodation and attraction activities in the selected plan. 
                Activities that already have a Google Maps link will be skipped.
              </Alert>
              <Form.Group className="mb-3">
                <Form.Label>Select Plan</Form.Label>
                <Form.Select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  disabled={isProcessingPlan}
                >
                  <option value="">-- Select a plan --</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button
                variant="primary"
                onClick={handleAddGoogleMapsLinks}
                disabled={!selectedPlanId || isProcessingPlan}
              >
                {isProcessingPlan ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Processing...
                  </>
                ) : (
                  'Add Google Maps Links to Plan'
                )}
              </Button>
              {processResult && (
                <Alert 
                  variant={processResult.success ? 'success' : 'danger'} 
                  className="mt-3"
                >
                  {processResult.message}
                </Alert>
              )}
            </Card.Body>
          </Card>
          
          <Card>
            <Card.Header>Technical Information</Card.Header>
            <Card.Body>
              <p><strong>App Version:</strong> {process.env.REACT_APP_VERSION || '1.0.0'}</p>
              <p><strong>User Agent:</strong> {navigator.userAgent}</p>
              <p><strong>Screen Size:</strong> {window.innerWidth}x{window.innerHeight}</p>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </div>
  );
} 